import React from 'react';
import { useInsertAtCursor } from './useInsertAtCursor';
import { TokenPickerPopover } from './TokenPickerPopover';

/**
 * Type-to-insert for referenceable `{{group.key}}` tokens on a CONTROLLED (`value`/`onChange(next)`) field.
 * Watches for the author typing `{{`: on that trigger it auto-completes the closing `}}` (so the pair is
 * never left unbalanced) and opens {@link TokenPickerPopover} — a searchable picker of every variable
 * available at this authoring point — anchored to the field. Choosing one replaces the whole `{{…}}`
 * region (braces plus anything the author typed between them) with the canonical `{{group.key}}` spelling,
 * the same tokens {@link resolveTokens} resolves on screen / PDF / Word / AI grounding.
 *
 * This is the variant for fields that already speak the runner's `onChange: (next: string) => void`
 * contract (e.g. {@link TokenTextField}). For arbitrary MUI `TextField` call sites whose `onChange` is
 * event-based, use the drop-in {@link TokenAwareTextField} instead. Returns the pieces a host field
 * spreads: `ref` for `inputRef`, `handleChange` for `onChange`, the `popover` node, and `insert` (the
 * caret-splice used by the companion button). The trigger is off while `enabled` is false.
 */
export interface UseTokenAutocompleteArgs {
  value: string;
  onChange: (next: string) => void;
  campaignId: number | undefined;
  /** The order of the step being authored; hides not-yet-available dynamic options. Omit = unrestricted. */
  uptoStepOrder?: number;
  enabled?: boolean;
}

export function useTokenAutocomplete<
  T extends HTMLInputElement | HTMLTextAreaElement = HTMLTextAreaElement,
>(args: UseTokenAutocompleteArgs) {
  const { value, onChange, campaignId, uptoStepOrder, enabled = true } = args;
  const { ref, insert } = useInsertAtCursor<T>();

  // The freshest value for the deferred pick: the popover click lands after the field's state updates,
  // so closing over the render-time `value` would splice into a stale string.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const [open, setOpen] = React.useState(false);
  // Index of the first `{` of the `{{` that opened the picker, so a pick can replace the whole region.
  const braceRef = React.useRef(0);

  const setCaret = React.useCallback(
    (pos: number) => {
      requestAnimationFrame(() => {
        const node = ref.current;
        if (!node) return;
        node.focus();
        try {
          node.setSelectionRange(pos, pos);
        } catch {
          /* setSelectionRange throws on some input types; caret restore is best-effort. */
        }
      });
    },
    [ref],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<T>) => {
      const next = e.target.value;
      const caret = e.target.selectionStart ?? next.length;

      // A single typed `{` that completes `{{` immediately before the caret opens the picker. Length
      // check keeps a paste of "{{" from triggering, and keeps deletes/replacements inert.
      const typedSingleChar = next.length === value.length + 1;
      const justOpenedBraces = caret >= 2 && next[caret - 1] === '{' && next[caret - 2] === '{';

      if (enabled && typedSingleChar && justOpenedBraces) {
        braceRef.current = caret - 2;
        const alreadyClosed = next.slice(caret, caret + 2) === '}}';
        const finalValue = alreadyClosed ? next : next.slice(0, caret) + '}}' + next.slice(caret);
        onChange(finalValue);
        setOpen(true);
        return;
      }

      onChange(next);
    },
    [enabled, onChange, value.length],
  );

  const close = React.useCallback(() => setOpen(false), []);

  const pick = React.useCallback(
    (token: string) => {
      const v = valueRef.current;
      const bracePos = braceRef.current;
      // Replace the whole `{{…}}` region — the braces plus any partial the author typed between them —
      // with the chosen canonical token, so a half-typed spelling never lingers next to the inserted one.
      const closeIdx = v.indexOf('}}', bracePos + 2);
      const endIdx = closeIdx === -1 ? Math.min(bracePos + 2, v.length) : closeIdx + 2;
      const nextValue = v.slice(0, bracePos) + token + v.slice(endIdx);
      onChange(nextValue);
      setOpen(false);
      setCaret(bracePos + token.length);
    },
    [onChange, setCaret],
  );

  const popover = (
    <TokenPickerPopover
      open={open && enabled}
      anchorEl={ref.current}
      campaignId={campaignId}
      uptoStepOrder={uptoStepOrder}
      onPick={pick}
      onClose={close}
    />
  );

  return { ref, insert, handleChange, popover };
}

export default useTokenAutocomplete;
